import React, { useCallback } from "react";
import { Row } from "antd";
import { Navigate, useLocation } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { isEmpty } from "lodash";

import { useAuth } from "../../../context/AuthContext";
import { getPostLoginPath } from "utils/auth-redirect";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useGetUserByIdQuery } from "../../../graphql/queries/get-user-by-id.generated";
import { useBrand } from "context/BrandContext";
import { useNavProfile } from "hooks/useNavProfile";

interface LoginLayoutProps {
	title: string;
	logoStyle?: React.CSSProperties | undefined;
	cardStyle?: React.CSSProperties | undefined;
	childrenLoading?: boolean;
	children: JSX.Element;
	className?: string;
}

const LoginLayout = (props: LoginLayoutProps) => {
	const { title, children, logoStyle, cardStyle, childrenLoading, className } = props;
	const { search } = useLocation();
	// Every auth screen renders through this layout, so this is also where a user who is already
	// signed in gets sent on: to the page that sent them here, when there was one.
	const postLoginPath = getPostLoginPath(search);
	const { loggedUser } = useAuth();
	const { updateLoggedUser, loggedUserValue } = useLoggedUser();
	const { theme } = useBrand();
	// Domain based, so it is known before the user is loaded.
	const { navProfile } = useNavProfile();
	const isOllieBrand = navProfile.sidebarBrand === "ollie";
	const topLevelClassName = `login-container${className ? ` ${className}` : ""}`;
	const copyrightNotice = `© ${new Date().getFullYear()} Highnote, Inc.`;

	const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
		console.log("LoginLayout Error Get Users: ", error);
		if (error.graphQLErrors) {
			error.graphQLErrors.forEach(graphQLError => {
				if (graphQLError.extensions?.code === "validation-failed") {
					console.log("Field with insufficient permissions:", graphQLError.message);
				} else {
					console.log("Error not related to permissions:", graphQLError);
				}
			});
		}
	}, []);

	const handleOnCompletedGetUsers = useCallback(
		data => {
			const { users_by_pk } = data;
			if (users_by_pk[0]) {
				updateLoggedUser(users_by_pk[0]);
			}
		},
		[updateLoggedUser]
	);

	// Skip the query if there's no firebase user or if we already have user data with matching IDs
	const shouldSkipQuery = !loggedUser?.uid || (loggedUserValue?.id === loggedUser.uid && !isEmpty(loggedUserValue));

	const { data, error, loading } = useGetUserByIdQuery({
		skip: shouldSkipQuery,
		variables: {
			id: loggedUser?.uid,
			is_not_deleted: true
		},
		errorPolicy: "all",
		fetchPolicy: "cache-first",
		nextFetchPolicy: "cache-first",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorsGetUsers,
		onCompleted: handleOnCompletedGetUsers
	});

	// If we have a firebase user and our stored user matches the firebase ID, we can redirect right away
	if (loggedUser && loggedUserValue?.id && loggedUserValue.id === loggedUser.uid) {
		return <Navigate to={postLoginPath} replace />;
	}

	// Otherwise, if we're loading user data or there is a data loading from the query, show loading
	if (loading) {
		return <LoadingOverlay isLoading={loading} />;
	}

	// If the query returned data, we should have updated the logged user state
	if (data?.users_by_pk[0]) {
		return <Navigate to={postLoginPath} replace />;
	}

	// Finally, if none of the above conditions are met, render the login form
	return (
		<div className={topLevelClassName}>
			<Row className="login-top-row">
				<div className="login-content-container">
					<div className={`login-logo-container${isOllieBrand ? " login-logo-container-ollie" : ""}`}>
						<img
							src={`${process.env.PUBLIC_URL}${isOllieBrand ? "/assets/images/icons/ollie-login-icon.svg" : theme.loginLogo}`}
							className="logo"
							style={logoStyle}
							width={250}
						/>
					</div>
					<div className="login-card" style={cardStyle}>
						{title && <span className="login-card-title">{title}</span>}
						<div className="login-children-container">{children}</div>
					</div>
				</div>
				<footer className="login-copyright-footer">
					<span className="login-copyright-text">{copyrightNotice}</span>
				</footer>
			</Row>

			<Row className="login-bottom-row"></Row>

			<LoadingOverlay isLoading={childrenLoading} />
		</div>
	);
};

export default LoginLayout;
